import { useMutation } from "@-/com.npmjs.tanstack__react-query";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import {
  getSimplifiedLevels,
  getTraditionalLevels,
} from "@-/dev.improve.haniverse.assets.graphemes.levels";
import { ActorDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.actor";
import { ContentDatabaseContext } from "@-/dev.improve.haniverse.contexts.database.content";
import { getTaiwanUsageMap } from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import { useClearActorDeck } from "@-/dev.improve.haniverse.lib.cards.deck.clear";
import { withDeadline } from "@-/dev.improve.haniverse.lib.deadline";
import { logger } from "@-/dev.improve.haniverse.lib.logger";
import {
  availableHanziScript,
  useTraditionalCharactersEnabled,
} from "@-/dev.improve.haniverse.lib.posthog";
import { clearPendingCompletedSlideKeys } from "@-/dev.improve.haniverse.lib.slide.read";
import { useActorUserDataQueryOptions } from "@-/dev.improve.haniverse.lib.user";
import { useState } from "react";
import {
  enrollLessonsBeforeLevel,
  type EnrollLessonsBeforeLevelResult,
} from "#enroll-lessons-before-level";

/**
 * Jumping deep into the curriculum enrolls thousands of cards one transaction at a time, far past
 * the default mutation backstop; the walk stops between lessons once this passes.
 */
const SET_CURRENT_LEVEL_DEADLINE_MS = 10 * 60_000;
/** Observe a walk still running from an unmounted caller with `useIsMutating`. */
export const SET_CURRENT_LEVEL_MUTATION_KEY = ["setCurrentLevel"];
/** Progress re-renders the caller; report it every this many lessons rather than every one. */
const PROGRESS_STEP = 25;

export type SetCurrentLevelProgress = { done: number; total: number };

/**
 * Admin superpower: make `levelIndex` (a `Level.index` in the user's script) the learner's current
 * level. Wipes the deck ({@link useClearActorDeck}), then replays every lesson before the level
 * ({@link enrollLessonsBeforeLevel}), so it moves the learner backwards as well as forwards.
 *
 * Returns the mutation plus the walk's `progress` while it runs. `onSuccess` runs even when the
 * caller unmounted mid-walk (it is the mutation's own callback, not a `mutate` one), so put
 * anything that must follow a finished walk there.
 */
export function useSetCurrentLevel({
  onSuccess,
}: { onSuccess?: (result: EnrollLessonsBeforeLevelResult) => void } = {}) {
  const clearActorDeckMutation = useClearActorDeck();
  const [actorDb] = useContextSelectors(ActorDatabaseContext, (c) => c.db);
  const [contentDb] = useContextSelectors(ContentDatabaseContext, (c) => c.db);
  const actorUserDataQueryOptions = useActorUserDataQueryOptions();
  const traditionalCharactersEnabled = useTraditionalCharactersEnabled();
  const [progress, setProgress] = useState<SetCurrentLevelProgress | null>(null);

  const mutation = useMutation({
    meta: { deadline: SET_CURRENT_LEVEL_DEADLINE_MS },
    mutationFn: async (
      { levelIndex }: { levelIndex: number },
      { client },
    ): Promise<EnrollLessonsBeforeLevelResult> =>
      withDeadline(
        { deadline: SET_CURRENT_LEVEL_DEADLINE_MS, operation: "set current level" },
        async (signal) => {
          setProgress(null);
          // Slide completions still queued would otherwise land after the reset below.
          clearPendingCompletedSlideKeys();
          await clearActorDeckMutation.mutateAsync();

          // The same script `nextLessonQueryFn` walks, so the position matches the Learn tab.
          const { user } = await client.fetchQuery(actorUserDataQueryOptions);
          const hanziScript = availableHanziScript(user.hanziScript, traditionalCharactersEnabled);
          const levels =
            hanziScript === "traditional" ? getTraditionalLevels() : getSimplifiedLevels();
          const result = await enrollLessonsBeforeLevel({
            actorDb,
            contentDb,
            hanziScript,
            levelIndex,
            levels,
            now: new Date(),
            onProgress: (done, total) => {
              if (done === total || done % PROGRESS_STEP === 0) {
                setProgress({ done, total });
              }
            },
            signal,
            twUsageMap: getTaiwanUsageMap(),
          });
          logger(import.meta.location).info(
            `[set-current-level] level ${levelIndex + 1} (${hanziScript}): enrolled ` +
              `${result.enrolledCount}/${result.totalCount} lessons, skipped ${result.skippedCount}`,
          );
          return result;
        },
      ),
    mutationKey: SET_CURRENT_LEVEL_MUTATION_KEY,
    onSettled: () => {
      setProgress(null);
    },
    onSuccess,
  });

  return { mutation, progress };
}
